<template>
  <div>
    <div class="upper-container">
      <div class="title-screen">
        <span>{{ adminText('workflowsAll.floor_plan_d5786d3', 'Floor plan') }}</span>
      </div>
      <p class="page-description">
        {{ adminText('workflowsAll.to_edit_your_seating_layout_choose_a_floor_pl_a32aba9', 'To edit your seating layout, choose a floor plan from the list below or upload your venue floor plan.') }}
      </p>
      <v-autocomplete
        v-model="selectedVenue"
        :items="workflowUi(venues)"
        :label="adminText('workflowsAll.venue_67cd406', 'Venue')"
        item-text="name"
        return-object
        box
        :disabled="isAnyTicketAlreadySoldOrBooked"
        @change="initialVenue.id !== selectedVenue.id ? (showChangeVenueMessage = true) : ''"
      />
      <div>
        <div class="subtitle">
          <span>{{ adminText('workflowsAll.edit_floor_plan_a391b0b', 'Edit floor plan') }}</span>
        </div>
        <p class="page-description">
          {{ adminText('workflowsAll.connect_your_tickets_to_seating_areas_click_t_8735a1a', 'Connect your tickets to seating areas. Click to select a specific seat, drag to select seats, then choose a ticket you want to connect.') }}
        </p>
      </div>
    </div>
    <div class="lower-container">
      <div class="floor-plan">
        <div class="floor-plan-title">{{ adminText('workflowsAll.floor_plan_d5786d3', 'Floor plan') }}</div>
        <div class="radio-buttons-container">
          <div class="floor-plan-toggle">
            <button
              type="button"
              :class="['floor-plan-toggle-btn', { 'floor-plan-toggle-btn--active': changeSelectOption === 1 }]"
              @click="handleSelectOptions(1)"
            >
              <img
                src="../../../public/assets/seat-logo.svg"
                :alt="adminText('workflowsAll.seats_05d560f', 'seats')"
                class="floor-plan-toggle-icon"
              >
              <span class="floor-plan-button-text">{{ adminText('workflowsAll.seat_b90b325', 'Seat') }}</span>
            </button>
            <button
              type="button"
              :class="['floor-plan-toggle-btn', { 'floor-plan-toggle-btn--active': changeSelectOption === 2 }]"
              @click="handleSelectOptions(2)"
            >
              <img
                src="../../../public/assets/row.svg"
                :alt="adminText('workflowsAll.rows_6c30d26', 'rows')"
                class="floor-plan-toggle-icon"
              >
              <span class="floor-plan-button-text">{{ adminText('workflowsAll.row_9bf7a8e', 'Row') }}</span>
            </button>
            <button
              type="button"
              :class="['floor-plan-toggle-btn', { 'floor-plan-toggle-btn--active': changeSelectOption === 3 }]"
              @click="handleSelectOptions(3)"
            >
              <img
                src="../../../public/assets/sectionsvg.svg"
                :alt="adminText('workflowsAll.sections_063b114', 'sections')"
                class="floor-plan-toggle-icon"
              >
              <span class="floor-plan-button-text">{{ adminText('workflowsAll.group_171a060', 'Group') }}</span>
            </button>
          </div>
        </div>
        <div id="spinner-svg-wrapper">
          <div v-if="loadingSvg" id="spinner-container">
            <spinner />
          </div>
          <div style="position: relative">
            <object
              id="svgfile"
              :data="workflowUi(initialVenue.imageUrl)"
              type="image/svg+xml"
              :style="floorPlanSvgStyle"
            />
            <div id="svg-action-buttons-wrapper" style="display: none">
              <v-icon id="zoomIn" class="material-icons" :size="43" :color="zoomInIconColor">
                add_circle
              </v-icon>
              <v-icon id="zoomOut" class="material-icons" :size="43" :color="zoomOutIconColor">
                remove_circle
              </v-icon>
              <v-icon id="resetZoom" class="material-icons" :size="43" :color="resetZoomIconColor">
                replay_circle_filled
              </v-icon>
            </div>
          </div>
        </div>
        <floor-plan-stats
          class="floor-plan-stats"
          :all-order-items="allOrderItems"
          :selected-seats="selectedSeats"
          :init-venue-availability="initVenueAvailability"
          :venue-seat-ids="selectedVenue ? selectedVenue.seats : []"
        />
      </div>
      <div>
        <floor-plan-ticket-view
          ref="floor-plan-ticket-view"
          :init-regular="regular"
          :init-table="table"
          :init-parter="parter"
          :selected-ticket="selectedTicket"
          :svg-element-type="svgElementType"
          :selected-seats="selectedSeats"
          @handleConnectSeatWithTicket="
            selectedTicket = $event
            handleConnectSeatWithTicket()
          "
          @handleSeatAvailability="handleSeatAvailability($event)"
        />
        <div v-if="showChangeVenueMessage" class="change-venue-msg-container">
          <h1 class="change-venue-title">{{ adminText('workflowsAll.change_venue_efedb48', 'Change venue') }}</h1>
          <p class="change-venue-description">
            {{ adminText('workflowsAll.are_you_sure_you_want_to_change_the_venue_thi_4b469fb', 'Are you sure you want to change the venue? This will permanently delete your current venue configuration.') }}
          </p>
          <div class="change-venue-buttons">
            <main-button :text="adminText('workflowsAll.cancel_77dfd21', 'Cancel')" outline @click="setPreviousVenue()" />
            <main-button :text="adminText('workflowsAll.change_64fbd99', 'Change')" color="#DB384C" @click="handleChangeVenue()" />
          </div>
        </div>
        <div v-if="showChangeVenueMessage" class="delete-message" @click="setPreviousVenue()" />
        <main-button
          style="margin-top: 36px"
          flat
          :disabled="isAnyTicketAlreadySoldOrBooked"
          @click="$emit('resetVenue')"
        >
          {{ adminText('workflowsAll.reset_995f2d9', 'RESET') }}
        </main-button>
      </div>
    </div>
    <simple-snackbar
      :open.sync="snackbarOpen"
      :message-prop="workflowLabel(snackbarMessage)"
      :color="snackbarColor"
    />
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar.vue'
import {
  disconnectOrderItemSectionSeats,
  getVenueAvailability,
  updateOrderItemSectionSeats,
  updateVenueAvailability,
} from '@/http'
import * as svgPanZoom from 'svg-pan-zoom'
import * as Hammer from 'hammerjs'
import FloorPlanTicketView from '@/components/tickets/FloorPlanTicketView.vue'
import { SeatState, SvgElementCEType, Venue } from '@/types'
import FloorPlanStats from '@/components/tickets/FloorPlanStats.vue'
import {
  ARENA_SECTION_COLOR_ZOOM_LIMIT,
  FLOOR_PLAN_COLORS,
} from '@/components/tickets/floorPlan.constants'
import { countSellableSeats } from '@/utils/floor-plan-quantity'

const getFloorPlanAssignableParterTickets = tickets =>
  (tickets || []).filter(
    ticket => !(ticket.itemMeta && ticket.itemMeta.parter && ticket.itemMeta.accessCode),
  )

export default {
  mixins: [WorkflowDisplay],
  name: 'create-floor-plan',
  components: {
    FloorPlanStats,
    FloorPlanTicketView,
    SimpleSnackbar,
  },
  props: {
    eventId: String,
    isAnyTicketAlreadySoldOrBooked: Boolean,
    user: Object,
    organizationSlug: String,
    initRegular: {
      type: Array,
      default: () => [],
    },
    initParter: {
      type: Array,
      default: () => [],
    },
    initTable: {
      type: Array,
      default: () => [],
    },
    venues: {
      type: Array,
      default: () => [],
    },
    initVenueAvailability: {
      type: Object,
      default: () => {},
    },
    initialVenue: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      snackbarOpen: false,
      snackbarMessage: '',
      snackbarColor: null,
      isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
      venueAvailability: this.initVenueAvailability,
      changeSelectOption: 1,
      svgElementType: null,
      parter: getFloorPlanAssignableParterTickets(this.initParter),
      table: this.initTable,
      selectedTicket: [],
      eventListenerType: { true: 'touchstart', false: 'click' },
      loadingSvg: true,
      zoomInIconColor: FLOOR_PLAN_COLORS.zoomIconActive,
      zoomOutIconColor: FLOOR_PLAN_COLORS.zoomIconActive,
      resetZoomIconColor: FLOOR_PLAN_COLORS.zoomIconActive,
      selectedSections: [],
      prevZoom: null,
      selectedVenue: null,
      maxZoom: 3,
      venueSections: [],
      regular: this.initRegular,
      showChangeVenueMessage: false,
      selectedSeats: [],
      seatToTicketMap: {},
      seatToTicketKeyMap: {},
      seatToTypeMap: {},
      sectionToSeatsMap: {},
      sectionToTicketMap: {},
      rowSectionToSeatsMap: {},
      arenaSectionVisualCache: [],
      arenaSectionZoomState: null,
      clickStartX: null,
      clickStartY: null,
    }
  },
  computed: {
    allOrderItems() {
      return [...this.regular, ...this.parter, ...this.table]
    },
    floorPlanSvgStyle() {
      if (!this.isArenaFanPitVenue()) {
        return { width: '100%' }
      }

      return {
        width: '100%',
        height: 'min(72vh, 720px)',
        objectFit: 'contain',
      }
    },
  },
  mounted() {
    this.initialVenue && (this.selectedVenue = this.initialVenue) && this.handleInitialVenue()
  },
  methods: {
    handleSelectOptions(option) {
      // if the clicked option is already active, do nothing
      if (this.changeSelectOption === option) {
        return
      }
      this.handleReset()
      this.changeSelectOption = option
    },
    setPreviousVenue() {
      this.showChangeVenueMessage = false
      this.selectedVenue = this.initialVenue
    },
    getSeatElement(svgDoc, seatId) {
      if (!svgDoc || !seatId) {
        return null
      }

      return svgDoc.getElementById(seatId)
    },
    getTicketIdsKey(tickets = []) {
      return [...new Set(tickets.map(ticket => ticket.id))].sort().join(',')
    },
    getSeatTicketIdsKey(seatId) {
      return this.seatToTicketKeyMap[seatId] || ''
    },
    getSelectedTicketIdsKey() {
      return Array.isArray(this.selectedTicket) ? this.getTicketIdsKey(this.selectedTicket) : ''
    },
    getSectionTickets(sectionId) {
      return this.sectionToTicketMap[sectionId] || []
    },
    isSeatBookedOrHeld(seatId) {
      const seatState = this.venueAvailability && this.venueAvailability[seatId]

      return !!seatState && [SeatState.SOLD, SeatState.HOLD].includes(seatState.state)
    },
    isSeatSelectableByAvailability(seatId) {
      return !this.isSeatBookedOrHeld(seatId)
    },
    isArenaSectionColorVenue() {
      return !!this.selectedVenue && this.selectedVenue.name === Venue.ARENA_ZAGREB
    },
    isArenaFanPitVenue() {
      return (
        !!this.selectedVenue &&
        [
          Venue.ARENA_ZAGREB_STOLOVI_FAN_PIT,
          Venue.ARENA_ZAGREB_STOLOVI_FAN_PIT_BEZ_GORNJIH_SEKTORA,
        ].includes(this.selectedVenue.name)
      )
    },
    isArenaFanPitTopLevelSectionElement(seatId) {
      return (
        this.isArenaFanPitVenue() &&
        typeof seatId === 'string' &&
        seatId.startsWith('Section-') &&
        !seatId.includes('_')
      )
    },
    getArenaSectionColorZoomLimit() {
      return ARENA_SECTION_COLOR_ZOOM_LIMIT
    },
    getArenaSectionPrimaryTicket(sectionId) {
      const sectionTickets = this.getSectionTickets(sectionId)

      if (!sectionTickets.length) {
        return null
      }

      return sectionTickets.reduce((preferredTicket, currentTicket) => {
        if (!preferredTicket) {
          return currentTicket
        }

        return Number(currentTicket.price) < Number(preferredTicket.price)
          ? currentTicket
          : preferredTicket
      }, null)
    },
    isArenaSectionAvailable(sectionId) {
      return (this.sectionToSeatsMap[sectionId] || []).some(seatId => {
        return this.isSeatSelectableByAvailability(seatId) && this.getSeatTicketIdsKey(seatId)
      })
    },
    getSectionColor: function (sectionId, svgDoc) {
      const sectionElement = this.getSeatElement(svgDoc, sectionId)
      const primaryTicket = this.getArenaSectionPrimaryTicket(sectionId)
      const sectionColor =
        (primaryTicket && primaryTicket.color) || FLOOR_PLAN_COLORS.sectionFallbackFill

      if (sectionElement) {
        sectionElement.style.fill = sectionColor
        sectionElement.setAttribute('fill', sectionColor)
      }

      return sectionColor
    },
    getArenaSectionVisual(sectionId, currZoom, zoomLimit = this.getArenaSectionColorZoomLimit()) {
      const primaryTicket = this.getArenaSectionPrimaryTicket(sectionId)
      const isAvailable = this.isArenaSectionAvailable(sectionId)
      if (!primaryTicket || !isAvailable) {
        if (Number(currZoom) <= zoomLimit) {
          return {
            fill: FLOOR_PLAN_COLORS.soldSeatFill,
            fillOpacity: 1,
          }
        }

        return {
          fill: 'transparent',
          fillOpacity: 1,
        }
      }

      if (Number(currZoom) <= zoomLimit) {
        return {
          fill: primaryTicket.color,
          fillOpacity: 0.3,
        }
      }

      return {
        fill: 'transparent',
        fillOpacity: 1,
      }
    },
    rebuildArenaSectionVisualCache(
      svgDoc,
      currZoom = this.getCurrZoom(),
      zoomLimit = this.getArenaSectionColorZoomLimit(),
    ) {
      if (!this.isArenaSectionColorVenue() || !svgDoc) {
        return
      }

      this.arenaSectionVisualCache = this.venueSections
        .map(sectionId => {
          const sectionElement = this.getSeatElement(svgDoc, sectionId)
          if (!sectionElement) {
            return null
          }

          return {
            sectionId,
            sectionElement,
            zoomedOutVisual: this.getArenaSectionVisual(sectionId, zoomLimit, zoomLimit),
            zoomedInVisual: this.getArenaSectionVisual(sectionId, currZoom + 1, zoomLimit),
          }
        })
        .filter(Boolean)
      this.arenaSectionZoomState = null
    },
    applyArenaSectionZoomColors(
      svgDoc,
      currZoom = this.getCurrZoom(),
      zoomLimit = this.getArenaSectionColorZoomLimit(),
    ) {
      if (!this.isArenaSectionColorVenue() || !svgDoc) {
        return
      }

      if (!this.arenaSectionVisualCache.length) {
        this.rebuildArenaSectionVisualCache(svgDoc, currZoom, zoomLimit)
      }

      const nextZoomState = Number(currZoom) <= zoomLimit ? 'zoomed-out' : 'zoomed-in'
      if (this.arenaSectionZoomState !== nextZoomState) {
        this.arenaSectionVisualCache.forEach(({ sectionElement, zoomedOutVisual, zoomedInVisual }) => {
          const { fill, fillOpacity } =
            nextZoomState === 'zoomed-out' ? zoomedOutVisual : zoomedInVisual

          sectionElement.style.fill = fill
          sectionElement.style.fillOpacity = fillOpacity
          sectionElement.setAttribute('fill', fill)
          sectionElement.setAttribute('fill-opacity', fillOpacity)
        })
        this.arenaSectionZoomState = nextZoomState
      }

      if (nextZoomState === 'zoomed-out') {
        this.selectedSections.forEach(sectionId => {
          const sectionElement = this.getSeatElement(svgDoc, sectionId)
          if (!sectionElement) {
            return
          }

          sectionElement.style.fill = FLOOR_PLAN_COLORS.selectedSeatFill
          sectionElement.style.fillOpacity = 1
          sectionElement.setAttribute('fill', FLOOR_PLAN_COLORS.selectedSeatFill)
          sectionElement.setAttribute('fill-opacity', 1)
        })
      }
    },
    canSelectSection: function (svgDoc, sectionId) {
      const seatsHaveSameTickets =
        this.getSelectedTicketIdsKey() === this.getTicketIdsKey(this.getSectionTickets(sectionId))
      if (!seatsHaveSameTickets && this.selectedSeats.length) {
        this.snackbarColor = 'error'
        this.snackbarOpen = true
        this.snackbarMessage = 'workflowsAll.selected_sections_have_different_tickets_59d3232'

        return
      }
      this.selectedSeats = [
        ...this.selectedSeats,
        ...this.selectedVenue.predefinedSections[sectionId],
      ]
      this.selectedTicket = this.getSectionTickets(sectionId)
      this.selectedSections = [...this.selectedSections, sectionId]
      const sectionElement = this.getSeatElement(svgDoc, sectionId)
      if (sectionElement) {
        sectionElement.style.fill = FLOOR_PLAN_COLORS.selectedSeatFill
        sectionElement.style.fillOpacity = 1
        sectionElement.setAttribute('fill', FLOOR_PLAN_COLORS.selectedSeatFill)
        sectionElement.setAttribute('fill-opacity', 1)
      }
    },
    sectionClick(svgDoc) {
      this.venueSections.forEach(sectionId => {
        const sectionElement = this.getSeatElement(svgDoc, sectionId)
        if (!sectionElement || sectionElement.dataset.sectionClickBound === 'true') {
          return
        }

        sectionElement.dataset.sectionClickBound = 'true'
        sectionElement.addEventListener(
          this.eventListenerType[this.isMobile],
          function () {
            if (
              window.panZoom.getZoom() <= this.getArenaSectionColorZoomLimit() &&
              this.changeSelectOption === 3
            ) {
              if (this.selectedSeats.some(s => s.includes(sectionId))) {
                this.selectedSeats = this.selectedSeats.filter(
                  seat => !this.selectedVenue.predefinedSections[sectionId].includes(seat),
                )
                this.selectedSections = this.selectedSections.filter(
                  section => section !== sectionId,
                )
                if (!this.selectedSections.length && !this.selectedSeats.length) {
                  this.selectedTicket = []
                }
              } else {
                this.canSelectSection(svgDoc, sectionId)
              }

              this.applyArenaSectionZoomColors(
                svgDoc,
                this.getCurrZoom(),
                this.getArenaSectionColorZoomLimit(),
              )
            }
          }.bind(this),
        )
      })
      if (this.prevZoom <= this.getArenaSectionColorZoomLimit()) {
        this.applyArenaSectionZoomColors(
          svgDoc,
          this.getCurrZoom(),
          this.getArenaSectionColorZoomLimit(),
        )
      }

      return true
    },
    hideSeatsAtSpecificZoom(zoomLimit, currZoom, svgDoc) {
      if (!this.isArenaSectionColorVenue()) {
        return
      }

      const oneColorSeats = this.getSeatElement(svgDoc, 'Seats_One_color')
      const accessibilitySeats = this.getSeatElement(svgDoc, 'Accessibility_seats')

      if (currZoom <= zoomLimit) {
        oneColorSeats && (oneColorSeats.style.display = 'none')
        accessibilitySeats && (accessibilitySeats.style.display = 'none')
        this.selectedSeats.forEach(seatId => this.colorSeat(seatId, svgDoc))
        this.sectionClick(svgDoc)
      } else {
        oneColorSeats && (oneColorSeats.style.display = 'unset')
        accessibilitySeats && (accessibilitySeats.style.display = 'unset')
        this.selectedSeats.forEach(seatId => {
          this.addWhiteColorOnElementSelect(seatId, svgDoc)
        })
      }

      this.applyArenaSectionZoomColors(svgDoc, currZoom, zoomLimit)
      this.prevZoom = window.panZoom.getZoom()
    },

    getCurrZoom() {
      return window.panZoom.getZoom()
    },
    handleReset() {
      const svgDoc = document.getElementById('svgfile').getSVGDocument()
      this.selectedSeats.forEach(seatId => this.colorSeat(seatId, svgDoc))
      const shouldRefreshArenaSections =
        svgDoc !== null &&
        this.isArenaSectionColorVenue() &&
        this.getCurrZoom() <= this.getArenaSectionColorZoomLimit()

      this.selectedSections = []
      this.selectedSeats = []
      this.emptyTicketsAndSvgElementType()

      if (shouldRefreshArenaSections) {
        this.applyArenaSectionZoomColors(
          svgDoc,
          this.getCurrZoom(),
          this.getArenaSectionColorZoomLimit(),
        )
      }
    },
    async handleConnectSeatWithTicket() {
      if (this.numberOfSeatsExceedsNumberOfTickets()) {
        this.snackbarColor = 'error'
        this.snackbarOpen = true
        this.snackbarMessage = { workflowCustomer: 'ticketQuantityError', params: {} }

        return
      }
      const { data, errors } = await updateOrderItemSectionSeats({
        orderItemId: this.selectedTicket.id,
        seats: this.selectedSeats,
        organizationSlug: this.organizationSlug,
        venueId: this.selectedVenue.id,
        eventId: this.eventId,
      })
      if (errors) {
        this.snackbarColor = 'error'
        this.snackbarOpen = true
        this.snackbarMessage = errors[0].extensions.originalError.message
        this.$emit('ticketSoldInTheMeantime')

        return
      }
      if (data) {
        const updatedVenueAvailability = await getVenueAvailability({ eventId: this.eventId })
        this.venueAvailability = {
          ...this.venueAvailability,
          ...updatedVenueAvailability.data.getVenueAvailability.availability,
        }
      }
      const ticketType = this.svgElementType
      this[ticketType] = this[ticketType].map(oi => {
        if (oi.id === data.updateOrderItemSectionSeats.id) {
          return {
            ...oi,
            sectionSeats: [...data.updateOrderItemSectionSeats.sectionSeats],
          }
        } else {
          return oi
        }
      })
      const svgDoc = document.getElementById('svgfile').getSVGDocument()
      this.buildLookupMaps(svgDoc)
      this.selectedSeats.forEach(seatId => this.colorSeat(seatId, svgDoc))
      this.venueSections.forEach(sectionId => this.colorSeat(sectionId, svgDoc))
      if (this.isArenaSectionColorVenue()) {
        this.rebuildArenaSectionVisualCache(svgDoc)
        this.hideSeatsAtSpecificZoom(
          this.getArenaSectionColorZoomLimit(),
          this.getCurrZoom(),
          svgDoc,
        )
      }
      this.selectedSeats = []
      this.selectedTicket = []
      this.svgElementType = null
      this.handleReset()
      setTimeout(() => {
        this.$refs['floor-plan-ticket-view'].setCountPerTicket()
      }, 1000)
    },
    colorSeat(seatId, svgDoc) {
      if (this.isArenaFanPitTopLevelSectionElement(seatId)) {
        return
      }

      const seatElement = this.getSeatElement(svgDoc, seatId)
      if (!seatElement) {
        return
      }

      // Venues list a section's outline id in its own predefined section only sometimes,
      // so outlines follow their whole section's tickets instead of their own linkage
      const isSectionOutline = !!this.sectionToSeatsMap[seatId]

      if (!isSectionOutline && this.isSeatBookedOrHeld(seatId)) {
        seatElement.style.fill = FLOOR_PLAN_COLORS.soldSeatFill
        seatElement.style.stroke = FLOOR_PLAN_COLORS.soldSeatStroke
        seatElement.setAttribute('fill', FLOOR_PLAN_COLORS.soldSeatFill)
        seatElement.setAttribute('stroke', FLOOR_PLAN_COLORS.soldSeatStroke)

        return
      }

      seatElement.removeAttribute('stroke')
      seatElement.style.stroke = ''
      const allTicketPerSeat = isSectionOutline
        ? this.getSectionTickets(seatId)
        : this.seatToTicketMap[seatId] || []
      const isSpecificSeat = seatId.includes('Row') && seatId.includes('Seat')
      let fillColor = FLOOR_PLAN_COLORS.defaultSeatFill

      if (allTicketPerSeat.length > 1) {
        fillColor = isSpecificSeat
          ? FLOOR_PLAN_COLORS.multiTicketFill
          : FLOOR_PLAN_COLORS.selectedSeatFill
      } else if (allTicketPerSeat.length === 1) {
        fillColor =
          isSpecificSeat
            ? allTicketPerSeat[0].color || FLOOR_PLAN_COLORS.fallbackTicketFill
            : FLOOR_PLAN_COLORS.selectedSeatFill
      }

      seatElement.style.fill = fillColor
      seatElement.setAttribute('fill', fillColor)
    },
    showDifferentTicketTypesErrorMsg() {
      this.snackbarColor = 'error'
      this.snackbarOpen = true
      this.snackbarMessage = 'workflowsAll.seats_that_are_not_selected_have_different_ti_8a0297a'
    },
    showTicketSoldErrorMsg(errors) {
      this.snackbarColor = 'error'
      this.snackbarOpen = true
      this.snackbarMessage = errors[0].extensions.originalError.message

      this.$emit('ticketSoldInTheMeantime')
    },
    showDifferentElementTypesErrorMsg() {
      this.snackbarColor = 'error'
      this.snackbarOpen = true
      this.snackbarMessage = 'workflowsAll.elements_that_are_not_selected_are_different__4994dbb'
    },
    async handleSeatAvailability(state) {
      const svgDoc = document.getElementById('svgfile').getSVGDocument()
      const svgElementType = this.getSvgElementType(svgDoc, this.selectedSeats[0])
      if (svgElementType === SvgElementCEType.PARTER) {
        const { errors } = await disconnectOrderItemSectionSeats({
          eventId: this.eventId,
          seats: this.selectedSeats,
          organizationSlug: this.organizationSlug,
          venueId: this.selectedVenue.id,
        })
        if (errors) {
          return this.showTicketSoldErrorMsg(errors)
        }
      } else {
        const { data, errors } = await updateVenueAvailability({
          eventId: this.eventId,
          seats: this.selectedSeats,
          organizationSlug: this.organizationSlug,
          state: state,
          venueId: this.selectedVenue.id,
        })
        if (errors) {
          return this.showTicketSoldErrorMsg(errors)
        }
        this.venueAvailability = {
          ...this.venueAvailability,
          ...data.updateVenueAvailability.availability,
        }
      }
      this.handleUpdateOrderItemsAndSvgElements(svgDoc)
    },
    handleUpdateOrderItemsAndSvgElements(svgDoc) {
      const ticketType = this.getSvgElementType(svgDoc, this.selectedSeats[0])
      const selectedSeatSet = new Set(this.selectedSeats)
      this[ticketType] = this[ticketType].map(oi => {
        return {
          ...oi,
          sectionSeats: oi.sectionSeats.filter(seatId => !selectedSeatSet.has(seatId)),
        }
      })
      this.buildLookupMaps(svgDoc)
      this.selectedSeats.forEach(seatId => this.colorSeat(seatId, svgDoc))
      this.venueSections.forEach(sectionId => this.colorSeat(sectionId, svgDoc))
      if (this.isArenaSectionColorVenue()) {
        this.rebuildArenaSectionVisualCache(svgDoc)
        this.applyArenaSectionZoomColors(
          svgDoc,
          this.getCurrZoom(),
          this.getArenaSectionColorZoomLimit(),
        )
      }
      this.selectedSeats = []
      this.emptyTicketsAndSvgElementType()
      this.$refs['floor-plan-ticket-view'].resetCurrSelectedSeatState()
    },
    getSvgElementType(svgDoc, id) {
      return this.seatToTypeMap[id]
    },
    inferSvgElementType(el, seatId) {
      if (el && el.getAttribute('ce-type')) {
        return el.getAttribute('ce-type')
      }

      if (
        this.isArenaFanPitVenue() &&
        seatId &&
        seatId.includes('Row-') &&
        seatId.includes('Seat-')
      ) {
        return SvgElementCEType.REGULAR
      }

      return null
    },
    getClickedSeatId(svgDoc, target) {
      if (!this.isArenaFanPitVenue()) {
        return (target && target.id) || ''
      }

      let currentElement = target
      while (currentElement && currentElement !== svgDoc) {
        const seatId = currentElement.id
        if (seatId && this.seatToTypeMap[seatId]) {
          return seatId
        }
        currentElement = currentElement.parentElement
      }

      return ''
    },
    getSeatRow(s) {
      return s.split('_').find(seatRow => seatRow.includes('Row-'))
    },
    getSeatSection(s) {
      return s.split('_').find(seatRow => seatRow.includes('Section-'))
    },
    getSeatRowSectionKey(seatId) {
      return `${this.getSeatRow(seatId) || ''}__${this.getSeatSection(seatId) || ''}`
    },
    checkIfSeatsHaveSameTickets(svgDoc, s) {
      return this.getSelectedTicketIdsKey() === this.getSeatTicketIdsKey(s)
    },
    checkIfSectionsHaveSameTickets(svgDoc, s) {
      const sectionId = s.split('_').find(str => str.includes('Section'))

      return this.getSelectedTicketIdsKey() === this.getTicketIdsKey(this.getSectionTickets(sectionId))
    },
    elementsAreSameType(svgDoc, next) {
      return this.svgElementType === next
    },
    setSelectedTickets(svgDoc, s) {
      const tickets = this.seatToTicketMap[s] || []
      this.selectedTicket = tickets

      return this.selectedTicket
    },
    emptyTicketsAndSvgElementType() {
      this.selectedTicket = []
      this.svgElementType = null
    },
    batchColorSeats(svgDoc, seatIds, colorFn, batchSize = 1000) {
      let i = 0
      const total = seatIds.length
      const processBatch = () => {
        const end = Math.min(i + batchSize, total)
        for (; i < end; i++) {
          const seatId = seatIds[i]
          colorFn.call(this, seatId, svgDoc)
        }
        if (i < total) {
          requestAnimationFrame(processBatch)
        }
      }
      processBatch()
    },
    initializeVenueMap(svgDoc) {
      if (this.isArenaSectionColorVenue()) {
        this.batchColorSeats(svgDoc, this.selectedVenue.seats, this.colorSeat)
      } else {
        this.selectedVenue.seats.forEach(seatId => this.colorSeat(seatId, svgDoc))
      }
      this.rebuildArenaSectionVisualCache(svgDoc)
      this.setZoomButtonsActions(svgDoc)
      this.hideSeatsAtSpecificZoom(this.getArenaSectionColorZoomLimit(), this.getCurrZoom(), svgDoc)
      const zoomControls = this.getSeatElement(svgDoc, 'svg-pan-zoom-controls')
      zoomControls && zoomControls.remove()
    },
    setZoomButtonsActions(svgDoc) {
      document.getElementById('zoomIn').addEventListener(
        this.eventListenerType[this.isMobile],
        function () {
          window.panZoom.zoomIn()
          this.checkZoomButtonsState(true)
          this.hideSeatsAtSpecificZoom(this.getArenaSectionColorZoomLimit(), this.getCurrZoom(), svgDoc)
        }.bind(this),
      )
      document.getElementById('zoomOut').addEventListener(
        this.eventListenerType[this.isMobile],
        function () {
          window.panZoom.zoomOut()
          this.checkZoomButtonsState(false)
          this.hideSeatsAtSpecificZoom(this.getArenaSectionColorZoomLimit(), this.getCurrZoom(), svgDoc)
        }.bind(this),
      )
      document.getElementById('resetZoom').addEventListener(
        this.eventListenerType[this.isMobile],
        function () {
          window.panZoom.resetZoom()
          window.panZoom.center()
          this.hideSeatsAtSpecificZoom(this.getArenaSectionColorZoomLimit(), this.getCurrZoom(), svgDoc)
          this.zoomInIconColor = FLOOR_PLAN_COLORS.zoomIconActive
          this.zoomOutIconColor = FLOOR_PLAN_COLORS.zoomIconActive
        }.bind(this),
      )
    },
    checkZoomButtonsState(isZoomingIn) {
      if (isZoomingIn) {
        if (window.panZoom.getZoom() >= this.maxZoom - 0.1) {
          this.zoomInIconColor = FLOOR_PLAN_COLORS.zoomIconDisabled
        }
        if (window.panZoom.getZoom() > 0.51) {
          this.zoomOutIconColor = FLOOR_PLAN_COLORS.zoomIconActive
        }
      } else {
        if (window.panZoom.getZoom() <= this.maxZoom - 0.1) {
          this.zoomInIconColor = FLOOR_PLAN_COLORS.zoomIconActive
        }
        if (window.panZoom.getZoom() <= 0.51) {
          this.zoomOutIconColor = FLOOR_PLAN_COLORS.zoomIconDisabled
        }
      }
    },
    numberOfSeatsExceedsNumberOfTickets() {
      // Parter elements represent zones, not individual ticket capacity.
      if (this.svgElementType === SvgElementCEType.PARTER) {
        return false
      }
      const orderItem = this[this.svgElementType].find(oi => oi.id === this.selectedTicket.id)
      const sectionSeats = new Set(orderItem.sectionSeats)

      // Match the backend: selected seats toggle their connection to this ticket.
      this.selectedSeats.forEach(seatId => {
        if (sectionSeats.has(seatId)) {
          sectionSeats.delete(seatId)
        } else {
          sectionSeats.add(seatId)
        }
      })

      // Connected seats include sold seats; quantity only represents remaining stock.
      return countSellableSeats([...sectionSeats]) > orderItem.initialQuantity
    },
    handleChangeVenue() {
      if (this.selectedVenue.id !== this.initialVenue.id) {
        this.$emit('changeVenue', this.selectedVenue)
      }
    },
    handleInitialVenue() {
      const svgObject = document.getElementById('svgfile')
      if (svgObject) {
        svgObject.addEventListener(
          'load',
          () => {
            this.hammerInit()
            const svgDoc = svgObject.getSVGDocument()
            this.buildLookupMaps(svgDoc)
            this.initializeVenueMap(svgDoc)
            const svgRoot = svgDoc.documentElement
            svgRoot.addEventListener(this.isMobile ? 'touchstart' : 'mousedown', this.handleSvgDown)
            svgRoot.addEventListener(this.isMobile ? 'touchend' : 'mouseup', this.handleSvgClick)
            this.loadingSvg = false
            document.getElementById('spinner-svg-wrapper').style.height = 'fit-content'
            document.getElementById('svg-action-buttons-wrapper').style.display = 'flex'
          },
          { once: true },
        )
      }
      this.handleReset()
    },
    hammerInit: function () {
      let eventsHandler
      eventsHandler = {
        haltEventListeners: ['touchstart', 'touchend', 'touchmove', 'touchleave', 'touchcancel'],
        init: function (options) {
          let instance = options.instance,
            initialScale = 1,
            pannedX = 0,
            pannedY = 0

          // Init Hammer
          // Listen only for pointer and touch events
          this.hammer = Hammer(options.svgElement, {
            inputClass: Hammer.SUPPORT_POINTER_EVENTS
              ? Hammer.PointerEventInput
              : Hammer.TouchInput,
          })

          // Enable pinch
          this.hammer.get('pinch').set({
            enable: true,
          })

          // Handle double tap
          this.hammer.on('doubletap', function () {
            instance.zoomIn()
          })

          // Handle pan
          this.hammer.on('panstart panmove', function (ev) {
            // On pan start reset panned variables
            if (ev.type === 'panstart') {
              pannedX = 0
              pannedY = 0
            }

            // Pan only the difference
            instance.panBy({
              x: ev.deltaX - pannedX,
              y: ev.deltaY - pannedY,
            })
            pannedX = ev.deltaX
            pannedY = ev.deltaY
          })

          // Handle pinch
          this.hammer.on('pinchstart pinchmove', function (ev) {
            // On pinch start remember initial zoom
            if (ev.type === 'pinchstart') {
              initialScale = instance.getZoom()
              instance.zoomAtPoint(initialScale * ev.scale, {
                x: ev.center.x,
                y: ev.center.y,
              })
            }

            instance.zoomAtPoint(initialScale * ev.scale, {
              x: ev.center.x,
              y: ev.center.y,
            })
          })

          // Prevent moving the page on some devices when panning over SVG
          options.svgElement.addEventListener('touchmove', function (e) {
            e.preventDefault()
          })
        },
        destroy: function () {
          this.hammer.destroy()
        },
      }

      let maxZoom = this.isMobile ? 5 : 3
      if (
        [
          Venue.ARENA_ZAGREB,
          'Bundek - Baš Naš',
          'Arena Zagreb 2',
          Venue.ARENA_ZAGREB_PARTER_EXTENDED,
          Venue.ARENA_ZAGREB_PARTER_A_B,
          'Sportska dvorana Gradski vrt',
          'Dvorana Gripe Split - stand up',
          'Arena Zagreb gimnastika žene',
          'Arena Zagreb gimnastika muškarci',
          Venue.KC_DRAZEN_PETROVIC,
        ].includes(this.selectedVenue.name)
      ) {
        if (this.isMobile) {
          maxZoom = 30
        } else {
          maxZoom = 7
        }
      }

      // Arena ZG - Stolovi / Fan Pit has dense, detailed seating, so allow a bit more zoom
      if (this.isArenaFanPitVenue()) {
        maxZoom = this.isMobile ? 12 : 6
      }
      this.maxZoom = maxZoom

      // Expose to window namespace for testing purposes
      window.panZoom = svgPanZoom('#svgfile', {
        zoomEnabled: true,
        controlIconsEnabled: true,
        maxZoom,
        fit: 1,
        center: 1,
        customEventsHandler: eventsHandler,
      })
    },
    addWhiteColorOnElementSelect(seatId, svgDoc) {
      const selectedElement = this.getSeatElement(svgDoc, seatId)
      if (!selectedElement) {
        return
      }

      let strokeWidth = 2
      if (this.isArenaSectionColorVenue()) {
        strokeWidth = 1
      } else if (selectedElement.hasAttribute('r')) {
        strokeWidth = Number(selectedElement.getAttribute('r')) * 0.4
      } else {
        strokeWidth = 2
      }
      selectedElement.style.fill = FLOOR_PLAN_COLORS.selectedSeatFill
      selectedElement.setAttribute('fill', FLOOR_PLAN_COLORS.selectedSeatFill)
      selectedElement.setAttribute('stroke', FLOOR_PLAN_COLORS.selectedSeatStroke)
      selectedElement.setAttribute('stroke-width', strokeWidth)
    },
    handleSvgDown(e) {
      if (e.touches && e.touches.length) {
        this.clickStartX = e.touches[0].clientX
        this.clickStartY = e.touches[0].clientY
      } else {
        this.clickStartX = e.clientX
        this.clickStartY = e.clientY
      }
    },
    handleSvgClick(e) {
      let endX, endY
      if (e.changedTouches && e.changedTouches.length) {
        endX = e.changedTouches[0].clientX
        endY = e.changedTouches[0].clientY
      } else {
        endX = e.clientX
        endY = e.clientY
      }
      const delta = 6
      if (Math.abs(endX - this.clickStartX) > delta || Math.abs(endY - this.clickStartY) > delta) {
        return // drag/pan, ne klik
      }
      const svgDoc = e.currentTarget
      const s = this.getClickedSeatId(svgDoc, e.target)
      if (!s) return
      if (!svgDoc.getElementById(s)) return
      // Only proceed if the clicked element has a ce-type (is a seat)
      const svgElementType = this.seatToTypeMap[s]
      if (!svgElementType) return
      // Prevent selection/click on SOLD or HOLD seats
      const seatStateObj = this.venueAvailability && this.venueAvailability[s]
      if (
        seatStateObj &&
        (seatStateObj.state === SeatState.SOLD || seatStateObj.state === SeatState.HOLD)
      ) {
        return
      }
      if (this.svgElementType === null) {
        this.svgElementType = svgElementType
      }
      const currSelectedSvgElementType = svgElementType
      if (this.changeSelectOption === 1) {
        if (this.selectedSeats.includes(s)) {
          this.setSelectedTickets(svgDoc, s)
          this.colorSeat(s, svgDoc)
          this.selectedSeats.splice(this.selectedSeats.indexOf(s), 1)
          this.emptyTicketsAndSvgElementType()
        } else {
          if (!this.elementsAreSameType(svgDoc, currSelectedSvgElementType)) {
            return this.showDifferentElementTypesErrorMsg()
          }
          if (!this.checkIfSeatsHaveSameTickets(svgDoc, s) && this.selectedSeats.length) {
            return this.showDifferentTicketTypesErrorMsg()
          }
          this.setSelectedTickets(svgDoc, s)
          this.addWhiteColorOnElementSelect(s, svgDoc)
          this.selectedSeats.push(s)
        }
      }
      if (this.changeSelectOption === 2) {
        const row = this.getSeatRow(s)
        const section = this.getSeatSection(s)
        const rowSectionKey = this.getSeatRowSectionKey(s)
        const seatRemove = []
        if (!this.elementsAreSameType(svgDoc, currSelectedSvgElementType)) {
          return this.showDifferentElementTypesErrorMsg()
        }
        if (this.selectedSeats.includes(s)) {
          this.setSelectedTickets(svgDoc, s)
          this.selectedSeats = this.selectedSeats.filter(sel => {
            const seatRow = this.getSeatRow(sel)
            const seatSection = this.getSeatSection(sel)
            if (seatRow === row && seatSection === section) {
              seatRemove.push(sel)
            }

            return (seatSection !== section && seatRow === row) || seatRow !== row
          })
          seatRemove.map(sr => {
            this.colorSeat(sr, svgDoc)
          })
          this.emptyTicketsAndSvgElementType()
        } else {
          if (!this.checkIfSeatsHaveSameTickets(svgDoc, s) && !!this.selectedSeats.length) {
            return this.showDifferentTicketTypesErrorMsg()
          }
          this.setSelectedTickets(svgDoc, s)
          const selectedSeatTicketsKey = this.getSelectedTicketIdsKey()
          let isThereTicketSeatDifferences = false
          const newSelectedSeats = (this.rowSectionToSeatsMap[rowSectionKey] || []).filter(seatId => {
            const seatTicketKey = this.getSeatTicketIdsKey(seatId)
            if (selectedSeatTicketsKey !== seatTicketKey) {
              isThereTicketSeatDifferences = true
            }

            return this.isSeatSelectableByAvailability(seatId) && selectedSeatTicketsKey === seatTicketKey
          })
          this.selectedSeats = [...this.selectedSeats, ...newSelectedSeats]
          if (isThereTicketSeatDifferences) {
            this.showDifferentTicketTypesErrorMsg()
          }
          newSelectedSeats.map(rs => {
            this.addWhiteColorOnElementSelect(rs, svgDoc)
          })
        }
      }
      if (this.changeSelectOption === 3) {
        const section = s.split('_').find(str => str.includes('Section'))
        if (!section) return
        if (this.selectedSeats.includes(s)) {
          this.setSelectedTickets(svgDoc, s)
          this.sectionToSeatsMap[section].forEach(seat => {
            if (this.selectedSeats.includes(seat)) {
              this.selectedSeats.splice(this.selectedSeats.indexOf(seat), 1)
              this.colorSeat(seat, svgDoc)
            }
          })
          this.selectedSections = this.selectedSections.filter(sec => sec !== section)
          this.emptyTicketsAndSvgElementType()
        } else {
          if (!this.elementsAreSameType(svgDoc, currSelectedSvgElementType)) {
            return this.showDifferentElementTypesErrorMsg()
          }
          if (!this.checkIfSectionsHaveSameTickets(svgDoc, s) && this.selectedSeats.length) {
            return this.showDifferentTicketTypesErrorMsg()
          }
          this.setSelectedTickets(svgDoc, s)
          const selectedTicketIdsKey = this.getSelectedTicketIdsKey()
          let isThereTicketSeatDifferences = false
          const newSelectedSeats = (this.sectionToSeatsMap[section] || []).filter(seatId => {
            const seatTicketKey = this.getSeatTicketIdsKey(seatId)
            if (selectedTicketIdsKey !== seatTicketKey) {
              isThereTicketSeatDifferences = true
            }

            return this.isSeatSelectableByAvailability(seatId) && selectedTicketIdsKey === seatTicketKey
          })
          this.selectedSeats = [...this.selectedSeats, ...newSelectedSeats]
          if (isThereTicketSeatDifferences) {
            this.showDifferentTicketTypesErrorMsg()
          }
          this.selectedSections = [...this.selectedSections, section]
          newSelectedSeats.map(seat => this.addWhiteColorOnElementSelect(seat, svgDoc))
        }
      }
    },
    buildLookupMaps(svgDoc) {
      this.seatToTypeMap = {}
      this.seatToTicketMap = {}
      this.seatToTicketKeyMap = {}
      this.sectionToSeatsMap = {}
      this.sectionToTicketMap = {}
      this.rowSectionToSeatsMap = {}
      this.arenaSectionVisualCache = []
      this.arenaSectionZoomState = null
      this.venueSections = Object.keys((this.selectedVenue && this.selectedVenue.predefinedSections) || {})

      this.selectedVenue.seats.forEach(seatId => {
        const el = this.getSeatElement(svgDoc, seatId)
        if (el) {
          this.seatToTypeMap[seatId] = this.inferSvgElementType(el, seatId)
        }

        const rowSectionKey = this.getSeatRowSectionKey(seatId)
        if (!this.rowSectionToSeatsMap[rowSectionKey]) {
          this.rowSectionToSeatsMap[rowSectionKey] = []
        }
        this.rowSectionToSeatsMap[rowSectionKey].push(seatId)
      })

      this.allOrderItems.forEach(orderItem => {
        const sectionSeats = orderItem.sectionSeats || []
        sectionSeats.forEach(seatId => {
          if (!this.seatToTicketMap[seatId]) {
            this.seatToTicketMap[seatId] = []
          }
          this.seatToTicketMap[seatId].push(orderItem)
        })
      })

      Object.entries((this.selectedVenue && this.selectedVenue.predefinedSections) || {}).forEach(
        ([sectionId, seatArr]) => {
          this.sectionToSeatsMap[sectionId] = seatArr
          const uniqueTicketsById = {}

          seatArr.forEach(seatId => {
            const seatTickets = this.seatToTicketMap[seatId] || []
            seatTickets.forEach(ticket => {
              uniqueTicketsById[ticket.id] = ticket
            })
          })

          this.sectionToTicketMap[sectionId] = Object.values(uniqueTicketsById)
        },
      )

      Object.entries(this.seatToTicketMap).forEach(([seatId, tickets]) => {
        this.seatToTicketKeyMap[seatId] = this.getTicketIdsKey(tickets)
      })

      this.venueSections.forEach(sectionId => {
        const sectionElement = this.getSeatElement(svgDoc, sectionId)
        if (sectionElement) {
          delete sectionElement.dataset.sectionClickBound
        }
      })

      if (this.isArenaSectionColorVenue()) {
        this.rebuildArenaSectionVisualCache(svgDoc)
      }
    },
  },
}
</script>

<style scoped>
p {
  margin-bottom: 0;
}

.page-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  margin-bottom: 1.5rem;
  color: #979797;
}

.subtitle {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 1.25rem;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #666666;
  margin-top: 3rem;
  margin-bottom: 1rem;
}

.upper-container {
  max-width: 42.75rem;
}

.floor-plan {
  width: 42.75rem;
  height: fit-content;
  border: 2px solid #f5f5f5;
  border-radius: 8px;
}

.floor-plan-title {
  width: 100%;
  height: 3.5rem;
  background: #f5f5f5;
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 1.25rem;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #666666;
  display: flex;
  align-items: center;
  text-indent: 1.5rem;
}

.radio-buttons-container {
  display: flex;
  margin: 16px 20px 16px;
}

.floor-plan-toggle {
  display: flex;
  gap: 6px;
  padding: 4px;
  background-color: #f5f5f5;
  border-radius: 8px;
}

.lower-container {
  display: flex;
  margin-top: 1.5rem;
  column-gap: 1.5rem;
}

@media screen and (max-width: 1331px) {
  .lower-container {
    flex-direction: column;
  }

  .floor-plan {
    width: unset;
    max-width: 42.75rem !important;
  }
}

@media screen and (max-width: 1012px) {
  .radio-buttons-container {
    flex-wrap: wrap;
    row-gap: 1rem;
  }
}

.change-venue-msg-container {
  width: 31.25rem;
  z-index: 1000;
  background: white;
  position: fixed;
  max-width: 80%;
  text-align: center;
  border-radius: 0.5rem;
  top: 25%;
  left: 50%;
  filter: drop-shadow(0px 0px 24px rgba(51, 51, 51, 0.2));
  padding: 1.25rem;
  transform: translate(-50%, 0);
}

.change-venue-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 1.25rem;
  letter-spacing: -0.03em;
  color: #0277bd;
  margin-bottom: 1.5rem;
}

.change-venue-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  color: #666666;
}

.change-venue-buttons {
  display: flex;
  justify-content: flex-end;
}

.delete-message {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: white;
  opacity: 80%;
  z-index: 500;
}

#svg-action-buttons-wrapper {
  display: flex;
  flex-direction: column;
  position: absolute;
  row-gap: 0.5rem;
  top: 20px;
  left: 21px;
}

.material-icons {
  user-select: none;
  cursor: pointer !important;
  background: white !important;
  height: fit-content;
  border-radius: 1000px !important;
  width: fit-content;
}

#spinner-container {
  height: 600px;
}

#spinner-svg-wrapper {
  height: 200px;
  overflow: hidden;
}

.floor-plan-stats {
  flex-wrap: wrap;
  gap: 8px;
}

.floor-plan-button-text {
  color: var(--Grey-10, #666);
  font-family: 'Source Sans Pro';
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  margin-left: 4px;
}

.floor-plan-toggle-btn {
  display: flex;
  align-items: center;
  column-gap: 4px;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
}

.floor-plan-toggle-btn--active {
  pointer-events: none;
  background: #fff;
  border: 1px solid #d9d9d9;
}

.floor-plan-toggle-btn--active .floor-plan-button-text {
  color: #0277bd;
}

/* Approximate blue tint for active SVG icons */
.floor-plan-toggle-btn--active .floor-plan-toggle-icon {
  filter: invert(32%) sepia(88%) saturate(1600%) hue-rotate(186deg) brightness(92%) contrast(105%);
}
</style>
